import * as React from 'react';

// "Give way instead of wrapping" — the design's own pattern (IMPLEMENTATION
// NOTES in STEP 2 and STEP 4): when a line's full text wouldn't fit on one
// row, a lower-priority piece is dropped or shortened rather than wrapped,
// and the decision is made by measuring the row, never by a media query (the
// column's width depends on card padding, the tick gutter and the font, not
// on the viewport).
//
// How it works, and why:
//  - An off-screen PROBE holds the FULL text at the live line's font. Always
//    measure the probe, never the live line — the live line changes once
//    something is hidden, which would make the answer flip back and forth.
//  - `measure(row, probe)` returns true when the full text doesn't fit. The
//    caller supplies the exact px allowances for its own row (padding, bullet,
//    gaps); change the layout and those numbers have to change with it.
//  - Re-measured after every render (layout effect, so the corrected state is
//    in place before paint and no wrapped frame is ever shown), on every
//    ResizeObserver callback on the row, and once document.fonts is ready
//    (Avenir is wider than the fallback, so measuring before it loads gives
//    the wrong answer).
//  - State only changes when the boolean actually changes (no render loops).
export default function useFit(measure) {
  const [tight, setTight] = React.useState(false);
  const els = React.useRef({ row: null, probe: null });
  const measureRef = React.useRef(measure);
  measureRef.current = measure;
  const observer = React.useRef(null);

  const run = React.useCallback(() => {
    const { row, probe } = els.current;
    if (!row || !probe || !row.isConnected) return;
    const next = !!measureRef.current(row, probe);
    setTight((prev) => (prev === next ? prev : next));
  }, []);

  const rowRef = React.useCallback((el) => {
    if (observer.current) { observer.current.disconnect(); observer.current = null; }
    els.current.row = el;
    if (el && typeof ResizeObserver !== 'undefined') {
      observer.current = new ResizeObserver(run);
      observer.current.observe(el);
    }
  }, [run]);

  const probeRef = React.useCallback((el) => { els.current.probe = el; }, []);

  React.useLayoutEffect(run);

  React.useEffect(() => {
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    return () => { if (observer.current) observer.current.disconnect(); };
  }, [run]);

  return { tight, rowRef, probeRef };
}
